import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';
import { getAllPosts, getAllTags, getCategoryList } from '../content/config';
import { searchPosts } from '../lib/search';

const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const categoryFilter = params.get('category') || '';
  const tagFilter = params.get('tag') || '';
  const allPosts = getAllPosts();

  const results = useMemo(() => {
    const baseResults = query.trim()
      ? searchPosts(query, allPosts).map((result) => allPosts.find((post) => post.slug === result.slug)).filter(Boolean)
      : allPosts;

    return baseResults.filter((post) => {
      const categoryOk = categoryFilter ? post.category === categoryFilter : true;
      const tagOk = tagFilter ? post.tags.includes(tagFilter) : true;
      return categoryOk && tagOk;
    });
  }, [allPosts, categoryFilter, query, tagFilter]);

  const onSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const q = String(formData.get('q') || '').trim();
    const nextParams = new URLSearchParams(params);
    nextParams.set('q', q);
    setParams(nextParams);
  };

  const updateFilter = (key, value) => {
    const nextParams = new URLSearchParams(params);
    if (value) {
      nextParams.set(key, value);
    } else {
      nextParams.delete(key);
    }
    setParams(nextParams);
  };

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-10">
      <Seo
        title="Recherche"
        description={query ? `Resultats pour ${query}` : 'Rechercher dans les contenus LMD'}
        path={`/recherche?q=${encodeURIComponent(query)}`}
      />

      <h1 className="lmd-editorial-title text-4xl font-bold text-slate-900">Recherche</h1>
      <p className="mt-2 text-slate-600">Le digital, expliqué autrement.</p>

      <form onSubmit={onSubmit} className="mt-6 flex gap-3">
        <input
          type="search"
          name="q"
          defaultValue={query}
          placeholder="Tapez un mot-cle..."
          className="w-full rounded-md border border-slate-300 px-4 py-2.5"
        />
        <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2.5 text-white">
          Rechercher
        </button>
      </form>

      <div className="mt-5 flex flex-wrap gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Categories</span>
        <button
          type="button"
          onClick={() => updateFilter('category', '')}
          className={`rounded-full px-3 py-1 text-xs ${!categoryFilter ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'}`}
        >
          Toutes
        </button>
        {getCategoryList().map((category) => (
          <button
            key={category.slug}
            type="button"
            onClick={() => updateFilter('category', category.slug)}
            className={`rounded-full px-3 py-1 text-xs ${
              categoryFilter === category.slug ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-700'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tags</span>
        <button
          type="button"
          onClick={() => updateFilter('tag', '')}
          className={`rounded-full px-3 py-1 text-xs ${!tagFilter ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}
        >
          Tous
        </button>
        {getAllTags().slice(0, 20).map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={() => updateFilter('tag', tag)}
            className={`rounded-full px-3 py-1 text-xs ${
              tagFilter === tag ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'
            }`}
          >
            #{tag}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {results.length === 0 ? (
          <p className="text-slate-500">Aucun resultat pour cette recherche.</p>
        ) : (
          results.map((post) => (
            <article key={post.slug} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                {post.categoryLabel} - {post.date}
              </p>
              <h2 className="mt-1 text-xl font-bold text-slate-900">
                <Link to={`/article/${post.slug}`} className="hover:text-indigo-600">
                  {post.title}
                </Link>
              </h2>
              <p className="mt-2 text-sm text-slate-600">{post.summary}</p>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default SearchPage;
